/**
 * Compact feed cards for the phone (docs/mobile-design.md §4.7): edge to edge, one header line, images at full
 * width, and one row of large actions. Issue, task and discussion cards keep their inline status line and the
 * newest comment; tapping a card opens the item. t184u: every card has reactions and comments; comments open the
 * item's own thread in a sheet (an acknowledgement's are its chat thread). t254u: a tap anywhere in a card's content
 * (title, description, the space around them) opens the item; its inner controls keep their own taps.
 */
import { createContext, useContext, useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Award, Building2, CalendarDays, CheckCircle2, CircleCheckBig, Gift, Hand, Megaphone, MessageCircle, MessagesSquare, Pin, Signal, Siren, Stamp } from 'lucide-react';
import { ackPageHref, feedBuildingCode, type FeedBuildingCode, earlierCommentsLabel, feedLastComment, feedThreadUnread, feedLatestText, feedLine, feedPost, feedStatus, viewAllCommentsLabel, severityLabel, type ActivitySeg, type Announcement, type FeedClosure, type FeedCommentPreview, type FeedItem, type FeedThread, type KudosPost } from '@tradeoutlet/shared';
import { taskKeys, useTaskComments, useAckAnnouncement, useAckMessage, useRespondShiftOffer, useRsvpEvent, type Rsvp } from '@tradeoutlet/data';
import { errorMessage } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { DATE_FORMATS } from '@/lib/format';
import { RelativeTime } from '@/components/time/RelativeTime';
import { ImageGallery } from '@/components/media/ImageGallery';
import { Avatar, Spinner } from '@/components/ui';
import { ITEM_REACTIONS, ItemReactions, useItemReactionToggle } from '@/components/reactions/ItemReactions';
import { useLongPress } from './longPress';
import { CommentsPreview, PinnedComposer, useCommentCount } from '@/features/threads/CommentsPreview';
import { queryClient } from '@/lib/queryClient';
import { TaskComments, ackThreadLink } from '@/features/home/feedShared';
import { BottomSheet } from './BottomSheet';
import { TaskCommentRow } from '@/features/tasks/TaskCommentRow';
import { useKudosBadges } from '@/features/people/badges';
import { BuildingLink } from './buildings/common';
import { cardOpenHandlers, cardTextProps } from '@/lib/cardOpen';
import { PostPhoto } from '@/features/home/FeedPost';
import { CLOSED_BODY, CLOSED_FADE, CLOSED_TITLE, FeedClosureStrip } from '@/features/home/FeedClosureStrip';

const ICON = { announcement: Megaphone, kudos: Award, birthday: Gift, ack: Stamp, shift_offer: Hand, task_completed: CircleCheckBig, issue: Siren, event: CalendarDays, discussion: MessagesSquare } as const;
const TONE: Record<FeedItem['kind'], string> = {
  announcement: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', kudos: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200',
  birthday: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200', ack: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  shift_offer: 'bg-accent-soft text-on-accent-soft', task_completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
  issue: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200', event: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  discussion: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};
export const bigBtn = 'inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-semibold disabled:opacity-60';
export const primaryBtn = `${bigBtn} bg-accent text-on-accent ring-1 ring-inset ring-black/10 active:bg-accent-pressed`;
export const secondaryBtn = `${bigBtn} border border-slate-300 text-slate-800 active:bg-slate-100 dark:border-slate-700 dark:text-slate-100 dark:active:bg-slate-800`;

const SEVERITY_TONE: Record<string, string> = { low: 'text-slate-400', medium: 'text-amber-500', high: 'text-orange-600', critical: 'text-red-600' };
/** t212u: the issue's severity as a small signal icon under the type icon (low grey, medium amber, high orange, critical red). */
export function SeverityIcon({ severity }: { severity: string }) {
  return <Signal role="img" aria-label={severityLabel(severity)} data-testid="mfeed-severity" className={clsx('h-4 w-4', SEVERITY_TONE[severity] ?? 'text-slate-400')} />;
}

/**
 * t212u: long-press anywhere on a card reacts to the item (the emoji + button is gone on phone cards; the pills stay
 * tappable). The release never taps what is under the finger.
 */
const CardReact = createContext<(() => void) | null>(null);
const CardOpen = createContext<() => void>(() => undefined);
/** t254u: a card's description; a tap opens the item through the card, Enter/Space opens it from the keyboard. */
function CardText({ className, testId, children }: { className: string; testId?: string; children: ReactNode }) {
  const open = useContext(CardOpen);
  return <p {...cardTextProps(open)} className={clsx('cursor-pointer', className)} data-testid={testId ?? 'mfeed-body'}>{children}</p>;
}
function ReactSheet({ type, id, onClose }: { type: string; id: string; onClose: () => void }) {
  const { toggle } = useItemReactionToggle(type, id);
  return (
    <BottomSheet open onClose={onClose} title="React" testId="mfeed-react-sheet">
      <div className="flex justify-around px-4 pb-3" role="group" aria-label="React">
        {ITEM_REACTIONS.map(e => <button key={e} type="button" onClick={() => { onClose(); void toggle(e); }} aria-label={`React with ${e}`} className="grid h-12 w-12 place-items-center rounded-full text-2xl active:bg-slate-100 dark:active:bg-slate-800">{e}</button>)}
      </div>
    </BottomSheet>
  );
}

function Shell({ item, title, meta, children, onOpen, testId, react, badge, wrapMeta, closure, building }: { item: FeedItem; title: ReactNode; meta: ReactNode; children?: ReactNode; onOpen: () => void; testId: string; react?: { type: string; id: string }; badge?: ReactNode; /** t298u: the meta line sits under the title, outside the open button, so its people and building open their own pages. */ wrapMeta?: boolean;
  /** t362u: a done / closed task or issue: a strip across the top, the rest quieter. */ closure?: FeedClosure | null;
  /** t701u: the building's short code, top right (full name on hover / for screen readers). */ building?: FeedBuildingCode | null }) {
  const Icon = ICON[item.kind];
  const [reacting, setReacting] = useState(false);
  const press = useLongPress(() => setReacting(true), Boolean(react));
  return (
    <article className="border-y border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900" data-testid={testId} data-kind={item.kind} data-closed={closure ? true : undefined} {...press} {...cardOpenHandlers(onOpen)}>
      {closure && <FeedClosureStrip closure={closure} className="-mx-4 -mt-3 mb-2.5" />}
      <button type="button" onClick={onOpen} className="flex w-full items-start gap-3 text-left" data-testid="mfeed-open">
        <span className="flex shrink-0 flex-col items-center gap-1">
          <span className={clsx('grid h-9 w-9 place-items-center rounded-full', TONE[item.kind], closure && CLOSED_FADE)} aria-hidden><Icon className="h-[18px] w-[18px]" /></span>
          {badge}
        </span>
        <span className="min-w-0 flex-1">
          <span className={clsx('line-clamp-2 block text-[15px] font-semibold leading-snug', closure && CLOSED_TITLE)} data-testid="mfeed-title">{title}</span>
          {!wrapMeta && <span className="mt-0.5 block truncate text-xs text-slate-500" data-testid="mfeed-meta">{meta}</span>}
        </span>
        {building && <span className="inline-flex shrink-0 items-center gap-1 pt-0.5 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300" title={building.name} aria-label={`Building: ${building.name}`} data-testid="post-building-code"><Building2 className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" aria-hidden data-testid="post-building-icon" />{building.code}</span>}
      </button>
      {wrapMeta && <p className="-mt-0.5 line-clamp-2 cursor-pointer pl-12 text-xs text-slate-500" data-testid="mfeed-meta">{meta}</p>}
      <CardOpen.Provider value={onOpen}><CardReact.Provider value={react ? () => setReacting(true) : null}>{children}</CardReact.Provider></CardOpen.Provider>
      {reacting && react && <ReactSheet type={react.type} id={react.id} onClose={() => setReacting(false)} />}
    </article>
  );
}
/**
 * t184u: reactions and the comments button under a card. Comments open the item's own thread in a sheet: the entity
 * thread (announcement, issue, event, shift offer, kudos), the task's comments, or for an acknowledgement its chat thread.
 */
export function ItemActions({ type, id, thread, to, item, total: knownTotal, post, canReply = true, canReact = true }: { type: string; id: string; thread?: FeedThread; to?: string; item?: FeedItem; total?: number; /** t298u: reactions first, the latest comment under them. */ post?: boolean;
  /** t522u: replies / reactions turned off for you on this item (announcements). */ canReply?: boolean; canReact?: boolean }) {
  const nav = useNavigate();
  const known = knownTotal ?? (type === 'task' || type === 'message' ? (thread?.count ?? 0) : undefined);
  const n = useCommentCount(type, id, known) ?? 0;
  const [open, setOpen] = useState(false);
  const label = n ? `${n}` : 'Comment';
  // t358u: not for your own comments, nor once you've commented after them.
  const unread = feedThreadUnread(thread);
  const openComments = () => (to ? nav(to) : setOpen(true));
  return (
    <>
    {item && !post && <CommentPreview item={item} total={n} onOpen={openComments} />}
    <div className="mt-1 flex items-center gap-1" data-testid="mfeed-actions" data-no-card-open>
      <ItemReactions type={type} id={id} size="lg" className="min-h-11 flex-1" pillsOnly canAdd={canReact} />
      {!n && <span className="flex-1" />}
      {(n > 0 || canReply) && <button type="button" onClick={openComments} data-testid="mfeed-comments" aria-label={n ? `${n} comments${unread ? ', new' : ''}` : type === 'message' ? 'Reply' : 'Comment'}
        className="relative ml-auto inline-flex h-11 shrink-0 items-center gap-1 px-2 text-sm text-slate-600 dark:text-slate-300">
        <MessageCircle className="h-4 w-4" aria-hidden />{type === 'message' ? 'Reply' : label}
        {unread && <span className="absolute right-0.5 top-2 h-2 w-2 rounded-full bg-rose-600" data-testid="mfeed-comments-unread" aria-hidden />}
      </button>}
      {open && (
        <BottomSheet open onClose={() => setOpen(false)} title="Comments" testId="mfeed-comments-sheet" scrollToEnd
          footer={type === 'task' ? <TaskComments th={{ ...(thread ?? { entity_type: 'task', entity_id: id, title: '', link: '', count: 0, actors: [], reply: { kind: 'task' } }), comments: [], events: [] }} onSent={() => void queryClient.invalidateQueries({ queryKey: taskKeys.comments(id) })} />
            : <PinnedComposer type={type} entityId={id} />}>
          <div className="px-4 pb-2">
            {type === 'task'
              ? <TaskCommentsSheet id={id} />
              : <CommentsPreview type={type} entityId={id} open hideButton max={50} hideComposer />}
          </div>
        </BottomSheet>
      )}
    </div>
    {item && post && <CommentPreview item={item} total={n} onOpen={openComments} post />}
    </>
  );
}

/** t189u: a task's own comments (long-press to react, as in chat) with the reply box under them. */
function TaskCommentsSheet({ id }: { id: string }) {
  const comments = useTaskComments(id);
  const list = comments.data?.filter(c => !c.deleted_at) ?? [];
  return (
    <div className="-mx-4" data-testid="m-task-comments">
      {comments.isLoading && <div className="py-2"><Spinner /></div>}
      {!comments.isLoading && !list.length && <p className="px-4 py-3 text-sm text-slate-500">No comments yet.</p>}
      {list.map(c => <TaskCommentRow key={c.id} c={c} />)}
    </div>
  );
}

/** t246u: an activity line's pieces; a person's name opens their profile (outside the card's open button). */
export function ActivityText({ segs, muted }: { segs: ActivitySeg[]; /** t298u: in a post's muted meta and Latest lines. */ muted?: boolean }) {
  const openPerson = useUi(s => s.openPerson);
  return <>{segs.map((x, n) => (typeof x === 'string' ? <span key={n}>{x}</span>
    : <button key={n} type="button" onClick={e => { e.stopPropagation(); openPerson(x.id); }} className={muted ? 'font-medium text-slate-600 dark:text-slate-300' : 'font-semibold text-slate-900 dark:text-white'} data-testid="mfeed-status-person">{x.name}</button>))}</>;
}
/** "Willow Parker set severity to High · Northwich" (t246u), "Done by James · Warehouse"; the building opens its page. */
function Status({ item }: { item: FeedItem }) {
  const s = feedStatus(item);
  if (!s) return null;
  const b = s.building;
  return (
    <div className="mt-1.5 pl-12" data-testid="mfeed-status">
      <p className="text-[13px] font-medium text-slate-700 dark:text-slate-200"><ActivityText segs={s.segs} />{b && <> · {b.id ? <BuildingLink id={b.id} name={b.name} /> : b.name}</>}</p>
    </div>
  );
}
/**
 * t212u / t225u: the latest comment, from the card's left edge, with "View N earlier comments" above it. A tap on
 * either opens the comments panel (as the speech bubble does); the name or avatar opens the person; a long-press
 * reacts (to the item) and never also counts as a tap.
 */
function CommentPreview({ item, total, onOpen, post }: { item: FeedItem; total: number; onOpen: () => void; post?: boolean }) {
  const c = feedLastComment(item);
  const react = useContext(CardReact);
  if (!c) return null;
  const earlier = post ? viewAllCommentsLabel(total, 1) : earlierCommentsLabel(total);
  return (
    <div className={post ? 'mb-1 border-t border-slate-100 pt-1.5 dark:border-slate-800' : 'mt-2'} data-testid="mfeed-status-comments" data-no-card-open>
      {earlier && <button type="button" onClick={onOpen} className="block py-0.5 text-left text-[13px] font-medium text-slate-500 dark:text-slate-400" data-testid="mfeed-earlier">{earlier}</button>}
      <LastComment c={c} onOpen={onOpen} onLong={react ?? undefined} />
    </div>
  );
}
/** t188u: the newest comment: small avatar and name (bold, dark; opens the person) apart from the text (regular, grey). */
function LastComment({ c, onOpen, onLong }: { c: FeedCommentPreview; onOpen?: () => void; onLong?: () => void }) {
  const openPerson = useUi(s => s.openPerson);
  const { byId } = useMembers();
  const who = c.actor_id;
  const press = useLongPress(() => onLong?.(), Boolean(onLong));
  return (
    <p role={onOpen ? 'button' : undefined} tabIndex={onOpen ? 0 : undefined} aria-label={onOpen ? `Open comments: ${c.name}${c.reply_to ? ` replied to ${c.reply_to}` : ''}: ${c.body}` : undefined}
      onClick={onOpen} onKeyDown={e => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }} {...press}
      className={clsx('flex min-w-0 items-center gap-1.5 py-0.5 text-[13px]', onOpen && 'cursor-pointer')} data-testid="mfeed-last-comment">
      {who
        ? <button type="button" onClick={e => { e.stopPropagation(); openPerson(who); }} className="flex shrink-0 items-center gap-1.5 font-semibold text-slate-950 dark:text-white" data-testid="mfeed-comment-author">
            <Avatar size={18} name={c.avatar_name ?? c.name} id={who} url={byId.get(who)?.avatar_url} />{c.name}
          </button>
        : <span className="shrink-0 font-semibold text-slate-950 dark:text-white">{c.name}</span>}
      <span className="min-w-0 truncate text-slate-500 dark:text-slate-400" data-testid="mfeed-comment-body">{c.reply_to && <span data-testid="mfeed-comment-reply">replied to {c.reply_to}: </span>}{c.body}</span>
    </p>
  );
}
/** Full-width image: bleeds to the screen edges (the card has 16px padding). */
function HeroImage({ a }: { a: Announcement }) {
  const [failed, setFailed] = useState(false);
  const img = a.preview_image;
  if (!img || failed) return null;
  const more = Math.max(0, (a.image_count ?? 1) - 1);
  return (
    <span className="relative -mx-4 mt-2.5 block bg-slate-100 dark:bg-slate-800" data-testid="mfeed-image">
      <img src={img.url} alt={img.alt} loading="lazy" decoding="async" onError={() => setFailed(true)} {...(img.width && img.height ? { width: img.width, height: img.height } : {})}
        className="block h-auto max-h-[70vh] w-full object-cover" />
      {more > 0 && <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-0.5 text-xs font-semibold text-white">+{more}</span>}
    </span>
  );
}

function AnnouncementCard({ item, a }: { item: FeedItem; a: Announcement }) {
  const nav = useNavigate();
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const ack = useAckAnnouncement(ws);
  return (
    <Shell item={item} testId="mfeed-announcement" onOpen={() => nav(`/announcements/${a.id}`)} react={a.can_react === false ? undefined : { type: 'announcement', id: a.id }}
      title={<>{a.pinned && <Pin className="mr-1 inline h-3.5 w-3.5 text-slate-500" aria-label="Pinned" />}{a.priority === 'urgent' && <span className="mr-1 text-rose-700">Urgent:</span>}{a.title}</>}
      meta={<>{a.author_name} · <RelativeTime iso={a.publish_at} /></>}>
      {a.excerpt && <CardText className="mt-1.5 line-clamp-3 text-sm text-slate-700 dark:text-slate-300">{a.excerpt}</CardText>}
      <Link to={`/announcements/${a.id}`} tabIndex={-1} aria-hidden className="block"><HeroImage a={a} /></Link>
      <Status item={item} />
      <ItemActions type="announcement" id={a.id} item={item} canReply={a.can_reply !== false} canReact={a.can_react !== false} />
      {a.ack_required && (
        <div className="mt-2.5 flex items-center gap-2" data-no-card-open>
          <span className="flex-1 text-xs font-medium text-amber-800 dark:text-amber-200">Please confirm you've read this</span>
          <button type="button" className={primaryBtn} disabled={ack.isPending} data-testid="mfeed-ack"
            onClick={() => ack.mutate(a.id, { onSuccess: () => toast('Thanks - acknowledged', 'success'), onError: e => toast(errorMessage(e), 'error') })}>
            {ack.isPending ? <Spinner className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}Acknowledge
          </button>
        </div>
      )}
    </Shell>
  );
}

/**
 * t247u: who gave the kudos, at the start of its meta line: small avatar and "From Aisha Lloyd · ". The name opens
 * their profile (and doesn't also open the post). Birthday and sales-target posts have no giver.
 */
export function KudosFrom({ k }: { k: Pick<KudosPost, 'author_id' | 'author_name' | 'kind'> }) {
  const openPerson = useUi(s => s.openPerson);
  const { byId } = useMembers();
  const id = k.author_id;
  if (!id || k.kind !== 'kudos') return null;
  const m = byId.get(id);
  const name = k.author_name ?? m?.display_name ?? 'Former member';
  const open = (e: SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); openPerson(id); };
  return (
    <span data-testid="kudos-from">
      <span className="mr-1 inline-flex h-4 align-top"><Avatar size={16} name={name} id={id} url={m?.avatar_url} /></span>
      From <span role="link" tabIndex={0} onClick={open} onKeyDown={e => { if (e.key === 'Enter') open(e); }} className="font-semibold text-slate-700 dark:text-slate-200" data-testid="kudos-from-name">{name}</span>{' · '}
    </span>
  );
}
function KudosCard({ item, k }: { item: FeedItem; k: KudosPost }) {
  const nav = useNavigate();
  const badges = useKudosBadges();
  const badge = k.badge ? badges.byKey.get(k.badge) : null;
  const to = [...k.recipients.map(r => r.display_name), ...k.teams.map(t => t.name)].join(', ');
  return (
    <Shell item={item} testId={`mfeed-${item.kind}`} onOpen={() => nav(`/kudos/${k.id}`)} react={{ type: 'kudos', id: k.id }}
      title={item.kind === 'birthday' ? `Happy birthday, ${to}` : <>{badge && <span className="mr-1" aria-hidden>{badge.emoji}</span>}Kudos to {to}</>}
      meta={<><KudosFrom k={k} />{badge ? `${badge.name} · ` : ''}<RelativeTime iso={k.created_at} /></>}>
      <CardText className="mt-1.5 line-clamp-4 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{k.body}</CardText>
      {k.attachments.length > 0 && <div className="-mx-4 mt-2.5 [&_*]:!max-w-none" data-testid="mfeed-image" data-no-card-open><ImageGallery images={k.attachments} max={430} /></div>}
      <Status item={item} />
      <ItemActions type="kudos" id={k.id} item={item} total={k.comment_count} />
    </Shell>
  );
}

function AckCard({ item }: { item: Extract<FeedItem, { kind: 'ack' }> }) {
  const nav = useNavigate();
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const ack = useAckMessage(ws);
  const a = item.ack;
  const overdue = Boolean(a.due_at && Date.parse(a.due_at) < Date.now());
  return (
    <Shell item={item} testId="mfeed-ack-card" onOpen={() => nav(ackPageHref(a.message_id))} title={a.preview} react={{ type: 'message', id: a.message_id }}
      meta={<>{a.author_name} in {a.conversation_name} · <RelativeTime iso={a.posted_at} /></>}>
      <div className="mt-2.5 flex items-center gap-2 pl-12" data-no-card-open>
        <span className={clsx('flex-1 text-xs font-medium', overdue ? 'text-rose-700 dark:text-rose-300' : 'text-amber-800 dark:text-amber-200')}>{overdue ? 'Overdue' : 'To acknowledge'}</span>
        <button type="button" className={primaryBtn} disabled={ack.isPending} data-testid="mfeed-ack-message"
          onClick={() => ack.mutate(a.message_id, { onSuccess: () => toast('Acknowledged', 'success'), onError: e => toast(errorMessage(e), 'error') })}>
          <CheckCircle2 className="h-4 w-4" />Acknowledge
        </button>
      </div>
      <ItemActions type="message" id={a.message_id} to={ackThreadLink(a)} />
    </Shell>
  );
}

function OfferCard({ item }: { item: Extract<FeedItem, { kind: 'shift_offer' }> }) {
  const nav = useNavigate();
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const answer = useRespondShiftOffer(ws);
  const o = item.offer;
  const tz = (o.shift as unknown as { timezone?: string }).timezone ?? 'Europe/London';
  const start = DateTime.fromISO(String(o.shift.starts_at), { zone: tz });
  const end = DateTime.fromISO(String(o.shift.ends_at), { zone: tz });
  const go = (action: 'accept' | 'decline') => answer.mutate({ id: o.id, action }, {
    onSuccess: r => toast(r.status === 'assigned' ? "It's yours - added to your shifts" : r.status === 'awaiting_confirmation' ? 'Thanks - your manager will confirm' : 'Declined', 'success'),
    onError: e => toast(errorMessage(e), 'error'),
  });
  return (
    <Shell item={item} testId="mfeed-offer" onOpen={() => nav(`/workforce/offers/${o.id}`)} react={{ type: 'shift_offer', id: o.id }}
      title={`${start.toFormat(DATE_FORMATS.weekday)} · ${start.toFormat(DATE_FORMATS.time)}–${end.toFormat(DATE_FORMATS.time)}`}
      meta={<>{o.sender_name ?? 'A manager'} offered you a shift · {o.shift.location_name}</>}>
      <div className="mt-2.5 flex gap-2 pl-12" data-no-card-open>
        <button type="button" className={primaryBtn} disabled={answer.isPending} onClick={() => go('accept')}>{o.kind === 'direct' || o.mode === 'first_accept' ? 'Accept' : 'I can do it'}</button>
        <button type="button" className={secondaryBtn} disabled={answer.isPending} onClick={() => go('decline')}>Decline</button>
      </div>
      <ItemActions type="shift_offer" id={o.id} item={item} />
    </Shell>
  );
}

function EventCard({ item }: { item: Extract<FeedItem, { kind: 'event' }> }) {
  const nav = useNavigate();
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const rsvp = useRsvpEvent(ws);
  const e = item.event;
  const [mine, setMine] = useState(e.my_rsvp);
  const start = DateTime.fromISO(e.starts_local, { zone: e.timezone });
  const end = DateTime.fromISO(e.ends_local, { zone: e.timezone });
  const when = e.all_day ? `${start.toFormat(DATE_FORMATS.weekday)} · All day` : `${start.toFormat(DATE_FORMATS.weekday)} · ${start.toFormat(DATE_FORMATS.time)}–${end.toFormat(DATE_FORMATS.time)}`;
  const bcode = feedBuildingCode(e.location_name, e.location_code); // t701u
  const answer = (r: Rsvp) => { const prev = mine; setMine(r); rsvp.mutate({ id: e.id, rsvp: r }, { onError: err => { setMine(prev); toast(errorMessage(err), 'error'); } }); };
  return (
    <Shell item={item} testId="mfeed-event" onOpen={() => nav(`/calendar/events/${e.id}`)} title={e.title} react={{ type: 'calendar_event', id: e.id }}
      building={bcode}
      meta={<>{when}{e.location_text ? ` · ${e.location_text}` : e.location_name && !bcode ? ` · ${e.location_name}` : ''}</>}>
      <Status item={item} />
      {mine !== null && (
        <div className="mt-2 flex gap-1.5 pl-12" role="group" aria-label="Are you going?" data-no-card-open>
          {(['accepted', 'tentative', 'declined'] as const).map(r => (
            <button key={r} type="button" onClick={() => answer(r)} aria-pressed={mine === r}
              className={clsx('h-11 flex-1 rounded-full border text-sm font-medium', mine === r ? 'border-ink bg-ink text-white dark:border-white dark:bg-white dark:text-ink' : 'border-slate-200 dark:border-slate-700')}>
              {r === 'accepted' ? 'Going' : r === 'tentative' ? 'Maybe' : 'Can’t go'}
            </button>
          ))}
        </div>
      )}
      <ItemActions type="calendar_event" id={e.id} item={item} thread={item.thread} />
    </Shell>
  );
}

/**
 * Issue, task done and discussion. t298u: a post about the subject: its title in bold with the ref muted, one muted
 * meta line (building · area · priority · status · reported by X · when), the description and first photo, at most one
 * "Latest: …" line, then reactions and the latest comment ("View all N comments" opens them).
 */
function ActivityCard({ item }: { item: FeedItem }) {
  const nav = useNavigate();
  const openTask = useUi(s => s.openTask);
  const l = feedLine(item);
  const p = feedPost(item)!;
  const task = /[?&]task=([\w-]+)/.exec(l.href);
  const ref = item.kind === 'issue' ? { type: 'incident', id: item.issue.id } : item.kind === 'task_completed' ? { type: 'task', id: item.task.id }
    : item.kind === 'discussion' ? { type: item.thread.entity_type, id: item.thread.entity_id } : null;
  // t701u: with a short code the building is in the corner, not the meta line (so meta[0] isn't the building).
  const building = item.kind === 'issue' && !p.building ? item.issue.location_id ?? null : null;
  const total = item.kind === 'issue' ? item.issue.comment_count : item.kind === 'task_completed' ? item.task.comment_count : undefined;
  return (
    <Shell item={item} testId={`mfeed-${item.kind}`} onOpen={() => (task ? openTask(task[1]!) : nav(l.href))} wrapMeta closure={p.closure} building={p.building}
      title={<>{p.title}{p.ref && <span className="ml-1.5 text-sm font-normal text-slate-500" data-testid="mfeed-ref">{p.ref}</span>}</>}
      meta={<>{p.meta.map((m, n) => <span key={n}>{n > 0 && ' · '}{n === 0 && building ? <BuildingLink id={building} name={String(m[0])} /> : <ActivityText segs={m} muted />}</span>)}
        {p.when && <>{p.meta.length ? ' · ' : ''}<RelativeTime iso={p.when} /></>}</>}
      react={ref ?? undefined} badge={item.kind === 'issue' ? <SeverityIcon severity={item.issue.severity} /> : undefined}>
      {(p.description || p.photo) && (
        <div className="mt-1 flex items-start gap-3 pl-12">
          {p.description && <CardText className={clsx('line-clamp-2 min-w-0 flex-1 text-sm text-slate-700 dark:text-slate-300', p.closure && CLOSED_BODY)} testId="mfeed-excerpt">{p.description}</CardText>}
          {p.photo && <PostPhoto incidentId={p.photo.incident_id} att={p.photo.attachment_id} className={clsx('h-14 w-14', !p.description && 'mr-auto', p.closure && CLOSED_FADE)} />}
        </div>
      )}
      {p.latest && (
        <p className="mt-1.5 pl-12 text-xs text-slate-500 dark:text-slate-400" data-testid="mfeed-latest" aria-label={feedLatestText(p.latest)}>
          Latest: {p.latest.who ? <><ActivityText segs={[p.latest.who]} muted /> {p.latest.what}</> : p.latest.what} <RelativeTime iso={p.latest.at} />
        </p>
      )}
      {ref ? <ItemActions type={ref.type} id={ref.id} thread={'thread' in item ? item.thread : undefined} item={item} total={total} post /> : null}
    </Shell>
  );
}

export function MobileFeedCard({ item }: { item: FeedItem }) {
  switch (item.kind) {
    case 'announcement': return <AnnouncementCard item={item} a={item.announcement} />;
    case 'kudos': case 'birthday': return <KudosCard item={item} k={item.kudos} />;
    case 'ack': return <AckCard item={item} />;
    case 'shift_offer': return <OfferCard item={item} />;
    case 'event': return <EventCard item={item} />;
    default: return <ActivityCard item={item} />;
  }
}
